import React, { useState, useRef, useCallback, useEffect } from 'react';

import LoadingSpinner from '../LoadingSpinner';

import DocumentHighlightOverlay, {
  type HighlightRect,
} from './DocumentHighlightOverlay';

interface ImageDocumentViewerProps {
  url: string;
  alt: string;
  highlights?: HighlightRect[];
  onHighlightClick?: (id: string) => void;
}

/**
 * Renders an image (JPEG/PNG) with a highlight overlay.
 * Tracks rendered dimensions via ResizeObserver for accurate bbox mapping.
 */
const ImageDocumentViewer: React.FC<ImageDocumentViewerProps> = ({
  url,
  alt,
  highlights = [],
  onHighlightClick,
}) => {
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);

  // Keep dimensions in sync on resize
  useEffect(() => {
    const img = imgRef.current;
    if (!img) return;

    const observer = new ResizeObserver(entries => {
      for (const entry of entries) {
        setDimensions({
          width: entry.contentRect.width,
          height: entry.contentRect.height,
        });
      }
    });
    observer.observe(img);
    return () => observer.disconnect();
  }, [isLoading]); // re-attach after image loads

  const handleLoad = useCallback(() => {
    const img = imgRef.current;
    if (img) {
      setDimensions({ width: img.clientWidth, height: img.clientHeight });
    }
    setIsLoading(false);
  }, []);

  const handleError = useCallback(() => {
    setError(true);
    setIsLoading(false);
  }, []);

  if (error) {
    return (
      <div className='flex items-center justify-center h-64 text-red-500 text-sm'>
        Failed to load image
      </div>
    );
  }

  return (
    <div className='relative inline-block'>
      {isLoading && (
        <div className='flex items-center justify-center h-64'>
          <LoadingSpinner size='lg' />
        </div>
      )}
      <img
        ref={imgRef}
        src={url}
        alt={alt}
        className='block max-w-full'
        onLoad={handleLoad}
        onError={handleError}
        style={{ display: isLoading ? 'none' : 'block' }}
      />
      {!isLoading && dimensions.width > 0 && (
        <DocumentHighlightOverlay
          highlights={highlights}
          containerWidth={dimensions.width}
          containerHeight={dimensions.height}
          onHighlightClick={onHighlightClick}
        />
      )}
    </div>
  );
};

export default ImageDocumentViewer;
